import { Alert, Button, Modal, Progress, Space, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export type NarrativeJobDto = {
  id: number;
  batchId: number;
  mode: string;
  status: string;
  total: number;
  done: number;
  failed: number;
  pending: number;
  running: number;
  finishedAt: string | null;
};

type JobResp = {
  enabled: boolean;
  job: NarrativeJobDto | null;
};

export function NarrativeJobBanner({ batchId }: { batchId?: number }) {
  const qc = useQueryClient();

  const { data } = useQuery<JobResp>({
    queryKey: ['narrative-job', batchId],
    enabled: !!batchId,
    queryFn: () => api.get('/reports/narrative/jobs', { params: { batchId } }),
    refetchInterval: (q) => {
      const job = q.state.data?.job;
      if (!job) return false;
      return job.status === 'PENDING' || job.status === 'RUNNING' ? 5000 : false;
    },
  });

  const trigger = useMutation({
    mutationFn: (mode: 'MANUAL_FILL' | 'MANUAL_FORCE') =>
      api.post('/reports/narrative/jobs', { batchId, mode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['narrative-job', batchId] });
      qc.invalidateQueries({ queryKey: ['personal-summary'] });
    },
  });

  if (!batchId || !data?.enabled) return null;

  const job = data.job;
  const inProgress = job?.status === 'PENDING' || job?.status === 'RUNNING';
  const completed =
    job?.status === 'DONE' && job.finishedAt != null;
  const progressPct =
    job && job.total > 0
      ? Math.round(((job.done + job.failed) / job.total) * 100)
      : 0;

  const confirmForce = () => {
    Modal.confirm({
      title: '强制全部重新生成 AI 报告？',
      content:
        '将为本批次范围内所有可出报告的员工重新调用大模型，可能产生额外费用并耗时较久。',
      okText: '确认重新生成',
      cancelText: '取消',
      onOk: () => trigger.mutateAsync('MANUAL_FORCE'),
    });
  };

  return (
    <Alert
      type={completed && job.failed > 0 ? 'warning' : completed ? 'success' : 'info'}
      showIcon
      style={{ marginBottom: 16 }}
      message={
        <Space wrap>
          <Typography.Text strong>AI 报告批量生成</Typography.Text>
          {inProgress && job && (
            <Typography.Text type="secondary">
              {job.status === 'PENDING' ? '排队中' : '生成中'}{' '}
              {job.done + job.failed}/{job.total}
              {job.failed > 0 ? ` · 失败 ${job.failed}` : ''}
            </Typography.Text>
          )}
          {completed && job && (
            <Typography.Text type="secondary">
              已完成 {job.done}/{job.total}
              {job.failed > 0 ? ` · 失败 ${job.failed} 人可补跑` : ''}
            </Typography.Text>
          )}
          {!job && (
            <Typography.Text type="secondary">
              核算完成后将自动生成；也可手动补跑
            </Typography.Text>
          )}
        </Space>
      }
      description={
        inProgress && job ? (
          <Progress percent={progressPct} size="small" status="active" />
        ) : null
      }
      action={
        <Space>
          <Button
            size="small"
            loading={trigger.isPending}
            disabled={inProgress}
            onClick={() => trigger.mutate('MANUAL_FILL')}
          >
            补生成缺失
          </Button>
          <Button
            size="small"
            danger
            loading={trigger.isPending}
            disabled={inProgress}
            onClick={confirmForce}
          >
            强制全部重新生成
          </Button>
        </Space>
      }
    />
  );
}
